import { useLayoutEffect, useMemo, useRef, useState } from "react";
import L, { type LeafletMouseEvent } from "leaflet";
import { AlertTriangle, Check, Crosshair, FileText, MapPin, Minus, Plus, RotateCcw, Trash2, Undo2 } from "lucide-react";
import { MapContainer, Marker, TileLayer, Tooltip, useMapEvents } from "react-leaflet";
import { setRunControlPoints } from "../api";
import { commitControlPoint, moveControlPoint, startControlPoint, type ControlPointDraft } from "../controlPoints";
import type { ControlPoint, MapRecord, ProcessingRun } from "../types";

interface ReviewViewProps {
  map: MapRecord;
  run: ProcessingRun;
  onRun: (run: ProcessingRun) => void;
  onHome: () => void;
}

interface PixelLike { x: number; y: number }

const MIN_IMAGE_ZOOM = 1;
const MAX_IMAGE_ZOOM = 4;

function MapClick({ enabled, onPick }: { enabled: boolean; onPick: (event: LeafletMouseEvent) => void }) {
  useMapEvents({ click: (event) => { if (enabled) onPick(event); } });
  return null;
}

function numberIcon(index: number, pending = false) {
  return L.divIcon({ className: "review-map-pin-wrap", html: `<span class="review-map-pin${pending ? " pending" : ""}">${index}</span>`, iconSize: [28, 28], iconAnchor: [14, 14] });
}

export function ReviewView({ map, run, onRun, onHome }: ReviewViewProps) {
  const review = run.result!.review!;
  const isImprovement = review.mode === "improvement";
  const [points, setPoints] = useState<ControlPoint[]>(run.result?.manual_control_points || []);
  const [draft, setDraft] = useState<ControlPointDraft | null>(null);
  const [imageZoom, setImageZoom] = useState(MIN_IMAGE_ZOOM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const imageCanvasRef = useRef<HTMLDivElement>(null);
  const imageZoomRef = useRef(MIN_IMAGE_ZOOM);
  const zoomAnchorRef = useRef<{ x: number; y: number; viewportX: number; viewportY: number } | null>(null);
  const touchPointsRef = useRef(new Map<number, { x: number; y: number }>());
  const touchPanRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number; moved: boolean } | null>(null);
  const pinchDistanceRef = useRef<number | null>(null);
  const suppressImageClickUntilRef = useRef(0);
  const bbox = review.bbox;
  const center = useMemo<[number, number]>(() => bbox ? [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] : [51.1657, 10.4515], [bbox]);
  const zoom = bbox ? 13 : 6;
  const effectivePoints = useMemo(() => commitControlPoint(points, draft), [points, draft]);
  const distribution = useMemo(() => analyzeDistribution(effectivePoints, review.map_bounds, review.marker_pixels || []), [effectivePoints, review.map_bounds, review.marker_pixels]);

  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const wrap = imageWrapRef.current;
    const canvas = imageCanvasRef.current;
    if (!anchor || !wrap || !canvas) return;
    wrap.scrollLeft = anchor.x * canvas.offsetWidth - anchor.viewportX;
    wrap.scrollTop = anchor.y * canvas.offsetHeight - anchor.viewportY;
    zoomAnchorRef.current = null;
  }, [imageZoom]);

  function changeImageZoom(nextZoom: number, clientX?: number, clientY?: number) {
    const wrap = imageWrapRef.current;
    const canvas = imageCanvasRef.current;
    const clamped = Math.min(MAX_IMAGE_ZOOM, Math.max(MIN_IMAGE_ZOOM, nextZoom));
    if (!wrap || !canvas || Math.abs(clamped - imageZoomRef.current) < 0.005) return;
    const wrapRect = wrap.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const focalX = clientX ?? wrapRect.left + wrap.clientWidth / 2;
    const focalY = clientY ?? wrapRect.top + wrap.clientHeight / 2;
    zoomAnchorRef.current = {
      x: Math.min(1, Math.max(0, (focalX - canvasRect.left) / canvasRect.width)),
      y: Math.min(1, Math.max(0, (focalY - canvasRect.top) / canvasRect.height)),
      viewportX: focalX - wrapRect.left,
      viewportY: focalY - wrapRect.top,
    };
    imageZoomRef.current = clamped;
    setImageZoom(clamped);
  }

  function handleImageWheel(event: React.WheelEvent<HTMLDivElement>) {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    changeImageZoom(imageZoomRef.current * Math.exp(-event.deltaY * 0.006), event.clientX, event.clientY);
  }

  function handleImagePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "touch" || (event.target as Element).closest(".review-zoom-controls")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    touchPointsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touchPointsRef.current.size === 1) {
      touchPanRef.current = { x: event.clientX, y: event.clientY, scrollLeft: event.currentTarget.scrollLeft, scrollTop: event.currentTarget.scrollTop, moved: false };
    } else if (touchPointsRef.current.size === 2) {
      pinchDistanceRef.current = touchDistance([...touchPointsRef.current.values()]);
      suppressImageClickUntilRef.current = Number.POSITIVE_INFINITY;
    }
  }

  function handleImagePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "touch" || !touchPointsRef.current.has(event.pointerId)) return;
    touchPointsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const touches = [...touchPointsRef.current.values()];
    if (touches.length >= 2) {
      event.preventDefault();
      const distance = touchDistance(touches);
      const previousDistance = pinchDistanceRef.current;
      if (previousDistance && distance > 0) {
        const centerX = (touches[0].x + touches[1].x) / 2;
        const centerY = (touches[0].y + touches[1].y) / 2;
        changeImageZoom(imageZoomRef.current * distance / previousDistance, centerX, centerY);
      }
      pinchDistanceRef.current = distance;
      return;
    }
    const pan = touchPanRef.current;
    if (!pan) return;
    const deltaX = event.clientX - pan.x;
    const deltaY = event.clientY - pan.y;
    if (Math.hypot(deltaX, deltaY) > 5) pan.moved = true;
    if (pan.moved) {
      event.preventDefault();
      event.currentTarget.scrollLeft = pan.scrollLeft - deltaX;
      event.currentTarget.scrollTop = pan.scrollTop - deltaY;
      suppressImageClickUntilRef.current = Date.now() + 400;
    }
  }

  function handleImagePointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "touch") return;
    const wasPinching = touchPointsRef.current.size >= 2 || pinchDistanceRef.current !== null;
    touchPointsRef.current.delete(event.pointerId);
    if (wasPinching) suppressImageClickUntilRef.current = Date.now() + 400;
    if (touchPointsRef.current.size < 2) pinchDistanceRef.current = null;
    const remaining = [...touchPointsRef.current.values()][0];
    touchPanRef.current = remaining
      ? { x: remaining.x, y: remaining.y, scrollLeft: event.currentTarget.scrollLeft, scrollTop: event.currentTarget.scrollTop, moved: false }
      : null;
  }

  function choosePixel(event: React.MouseEvent<HTMLDivElement>) {
    if (Date.now() < suppressImageClickUntilRef.current) return;
    const image = imageRef.current;
    if (!image) return;
    const rect = image.getBoundingClientRect();
    const x = (event.clientX - rect.left) * review.image_width / rect.width;
    const y = (event.clientY - rect.top) * review.image_height / rect.height;
    const next = startControlPoint(points, draft, { x, y });
    setPoints(next.points);
    setDraft(next.draft);
    setError("");
  }

  function chooseCoordinate(event: LeafletMouseEvent) {
    if (!draft) return;
    setDraft((current) => moveControlPoint(current, { lat: event.latlng.lat, lon: event.latlng.lng }));
    setError("");
  }

  async function submit() {
    if (effectivePoints.length < review.minimum_points) return;
    setSending(true); setError("");
    try { onRun(await setRunControlPoints(run.id, effectivePoints)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Kontrollpunkte konnten nicht gespeichert werden"); }
    finally { setSending(false); }
  }

  return <main className="review-layout">
    <header className="review-head">
      <div className="review-title"><span><Crosshair size={20} /></span><div><h1>{isImprovement ? "Genauigkeit verbessern" : "Kurzer Kartenabgleich nötig"}</h1><p>{isImprovement ? "Gleiche sichere Bildpunkte mit der echten Karte ab." : "Die Automatik hat bewusst keinen unsicheren Export veröffentlicht."}</p></div></div>
      <button className="start-over" onClick={onHome}><RotateCcw size={15} /> Abbrechen</button>
    </header>
    <div className="review-alert"><AlertTriangle size={19} /><div><strong>{isImprovement ? "So wird die Karte präziser" : "Warum wird nachgefragt?"}</strong><ul>{review.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div></div>
    <section className="review-workspace">
      <article className={`review-pane ${draft ? "selected" : ""}`}>
        <div className="review-pane-head"><span>1</span><div><strong>Punkt im Tourplan wählen</strong><small>Tippen zum Setzen · Zwei Finger oder Strg/Cmd + Mausrad zum Zoomen</small></div>{draft && <em><Check size={13} /> gewählt</em>}</div>
        <div className="review-image-wrap" ref={imageWrapRef} onWheel={handleImageWheel} onPointerDown={handleImagePointerDown} onPointerMove={handleImagePointerMove} onPointerUp={handleImagePointerEnd} onPointerCancel={handleImagePointerEnd}>
          <div className="review-zoom-controls" aria-label="Tourplan-Zoom"><div><button type="button" aria-label="Tourplan verkleinern" disabled={imageZoom <= MIN_IMAGE_ZOOM} onClick={() => changeImageZoom(imageZoomRef.current - .5)}><Minus size={15} /></button><span>{Math.round(imageZoom * 100)}%</span><button type="button" aria-label="Tourplan vergrößern" disabled={imageZoom >= MAX_IMAGE_ZOOM} onClick={() => changeImageZoom(imageZoomRef.current + .5)}><Plus size={15} /></button>{imageZoom > MIN_IMAGE_ZOOM && <button type="button" className="review-zoom-fit" onClick={() => changeImageZoom(MIN_IMAGE_ZOOM)}>Einpassen</button>}</div></div>
          <div ref={imageCanvasRef} className="review-image-canvas" style={{ width: `${imageZoom * 100}%` }} onClick={choosePixel} role="button" tabIndex={0} aria-label="Kontrollpunkt im Tourplan wählen">
            <img ref={imageRef} src={review.preview_url} alt="Gerenderter Tourplan" draggable={false} />
            {distribution.hull.length >= 3 && <svg className={`review-coverage-overlay ${distribution.good ? "good" : ""}`} viewBox={`0 0 ${review.image_width} ${review.image_height}`} preserveAspectRatio="none" aria-hidden="true"><polygon points={distribution.hull.map((point) => `${point.x},${point.y}`).join(" ")} /></svg>}
            {points.map((point, index) => <span className="review-image-pin" key={`${point.pixel_x}-${point.pixel_y}`} style={{ left: `${point.pixel_x / review.image_width * 100}%`, top: `${point.pixel_y / review.image_height * 100}%` }}>{index + 1}</span>)}
            {draft && <span className="review-image-pin pending" style={{ left: `${draft.x / review.image_width * 100}%`, top: `${draft.y / review.image_height * 100}%` }}>{points.length + 1}</span>}
          </div>
        </div>
      </article>
      <article className={`review-pane ${draft ? "ready" : ""}`}>
        <div className="review-pane-head"><span>2</span><div><strong>Gleichen Punkt auf der echten Karte wählen</strong><small>{draft?.lat !== null && draft?.lat !== undefined ? "Beliebig oft klicken oder den blauen Marker ziehen" : draft ? "Jetzt auf die passende Position klicken" : "Zuerst links einen Bildpunkt setzen"}</small></div>{draft?.lat !== null && draft?.lat !== undefined && <em><Check size={13} /> verschiebbar</em>}</div>
        <MapContainer className={`review-real-map ${draft ? "is-picking" : ""}`} center={center} zoom={zoom} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClick enabled={Boolean(draft)} onPick={chooseCoordinate} />
          {points.map((point, index) => <Marker key={`${point.lat}-${point.lon}`} position={[point.lat, point.lon]} icon={numberIcon(index + 1)}><Tooltip>{point.name}</Tooltip></Marker>)}
          {draft?.lat !== null && draft?.lat !== undefined && draft.lon !== null && <Marker position={[draft.lat, draft.lon]} icon={numberIcon(points.length + 1, true)} draggable eventHandlers={{ dragend: (event) => { const position = (event.target as L.Marker).getLatLng(); setDraft((current) => moveControlPoint(current, { lat: position.lat, lon: position.lng })); } }}><Tooltip permanent direction="top">Noch verschiebbar</Tooltip></Marker>}
        </MapContainer>
      </article>
    </section>
    <section className="review-footer">
      <div className="review-progress"><strong>{effectivePoints.length} / {review.recommended_points} Punkte</strong><span>{effectivePoints.length < review.minimum_points ? `Noch ${review.minimum_points - effectivePoints.length} erforderlich` : distribution.good ? "Gute räumliche Abdeckung" : "Bereit – weitere, weit entfernte Punkte verbessern das Ergebnis"}</span></div>
      <div className="review-point-list">{points.map((point, index) => <span key={index}><MapPin size={13} /> {index + 1}<button aria-label={`Punkt ${index + 1} entfernen`} onClick={() => setPoints((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={12} /></button></span>)}{draft?.lat !== null && draft?.lat !== undefined && <span className="pending"><MapPin size={13} /> {points.length + 1}<small>verschiebbar</small></span>}</div>
      <div className="review-actions">{draft && <button className="button button-ghost" onClick={() => setDraft(null)}><Undo2 size={15} /> Auswahl lösen</button>}<button className="button button-primary" onClick={() => void submit()} disabled={sending || effectivePoints.length < review.minimum_points}>{sending ? "Wird geprüft …" : "Kontrollpunkte prüfen"}</button></div>
      <div className={`review-distribution ${distribution.good ? "good" : ""}`}>
        <div><span>Kontrollpunktfläche</span><strong>{distribution.areaPercent}%</strong></div>
        <div><span>Höfe innerhalb</span><strong>{distribution.markerPercent === null ? "–" : `${distribution.markerPercent}%`}</strong></div>
        <p><Crosshair size={13} /> {distribution.guidance}</p>
      </div>
      {error && <span className="form-error">{error}</span>}
      <p className="review-privacy"><FileText size={13} /> Die Punkte werden nur mit dieser Karte auf deinem Server gespeichert.</p>
    </section>
  </main>;
}

function analyzeDistribution(points: ControlPoint[], mapBounds: [number, number, number, number], markers: PixelLike[]) {
  const pixels = points.map((point) => ({ x: point.pixel_x, y: point.pixel_y }));
  const hull = convexHull(pixels);
  const [x0, y0, x1, y1] = mapBounds;
  const mapArea = Math.max(1, (x1 - x0) * (y1 - y0));
  const areaPercent = Math.min(100, Math.round(polygonArea(hull) / mapArea * 100));
  const markerPercent = markers.length && hull.length >= 3
    ? Math.round(markers.filter((marker) => pointInPolygon(marker, hull)).length / markers.length * 100)
    : null;
  const good = points.length >= 4 && areaPercent >= 25 && (markerPercent === null || markerPercent >= 65);
  let guidance = "Setze Punkte an gut erkennbaren Stellen möglichst weit voneinander entfernt.";
  if (points.length >= 2 && !good) {
    const minX = Math.min(...pixels.map((point) => point.x));
    const maxX = Math.max(...pixels.map((point) => point.x));
    const minY = Math.min(...pixels.map((point) => point.y));
    const maxY = Math.max(...pixels.map((point) => point.y));
    const gaps = [
      { label: "oben", value: minY - y0 },
      { label: "unten", value: y1 - maxY },
      { label: "links", value: minX - x0 },
      { label: "rechts", value: x1 - maxX },
    ];
    const direction = gaps.sort((left, right) => right.value - left.value)[0].label;
    guidance = `Der nächste Punkt hilft am meisten weit ${direction}, außerhalb der blauen Fläche.`;
  } else if (good) {
    guidance = "Die Punkte umschließen den relevanten Kartenbereich ausreichend.";
  }
  return { hull, areaPercent, markerPercent, good, guidance };
}

function convexHull(points: PixelLike[]): PixelLike[] {
  if (points.length < 3) return points;
  const sorted = [...points].sort((left, right) => left.x - right.x || left.y - right.y);
  const cross = (origin: PixelLike, left: PixelLike, right: PixelLike) => (left.x - origin.x) * (right.y - origin.y) - (left.y - origin.y) * (right.x - origin.x);
  const lower: PixelLike[] = [];
  for (const point of sorted) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) lower.pop(); lower.push(point); }
  const upper: PixelLike[] = [];
  for (const point of [...sorted].reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) upper.pop(); upper.push(point); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

function polygonArea(points: PixelLike[]) {
  if (points.length < 3) return 0;
  return Math.abs(points.reduce((area, point, index) => { const next = points[(index + 1) % points.length]; return area + point.x * next.y - next.x * point.y; }, 0)) / 2;
}

function pointInPolygon(point: PixelLike, polygon: PixelLike[]) {
  let inside = false;
  for (let left = 0, right = polygon.length - 1; left < polygon.length; right = left++) {
    const a = polygon[left]; const b = polygon[right];
    if ((a.y > point.y) !== (b.y > point.y) && point.x < (b.x - a.x) * (point.y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function touchDistance(points: Array<{ x: number; y: number }>) {
  if (points.length < 2) return 0;
  return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
}
